import { ObjectPropertiesProvider } from '@trimble/web3d-providers';
import { Accessor, createEffect, createRoot, createSignal, on } from 'solid-js';
import { CapabilityBase } from './base/capability-base';
import { CapabilityCategory } from './base/models/capability-category';
import { isAbortError } from '@trimble/utilities';
import { asError, LoggerService } from '@trimble/logging';
import type { ObjectPropertiesPanelData } from './object-properties-panel-capability.types';
export type {
  ObjectPropertiesPanelCommonProperty,
  ObjectPropertiesPanelData,
  ObjectPropertiesPanelProperty,
  ObjectPropertiesPanelValue,
} from './object-properties-panel-capability.types';

/**
 * Single, global capability-like service for Object Properties panel state + data.
 * It owns panel command/open state and delegates selection->properties resolution
 * to `ObjectPropertiesProvider`.
 */
export class ObjectPropertiesPanelCapability extends CapabilityBase {
  public override readonly category: CapabilityCategory =
    CapabilityCategory.Command;

  private readonly logger = LoggerService.getInstance().createNamedLogger(
    'ObjectPropertiesPanelCapability'
  );

  public readonly isOpen: Accessor<boolean>;
  public readonly data: Accessor<ObjectPropertiesPanelData | undefined>;
  public readonly isLoading: Accessor<boolean>;

  private readonly setIsOpen: (next: boolean) => void;
  private readonly setData: (
    next: ObjectPropertiesPanelData | undefined
  ) => void;
  private readonly setIsLoading: (next: boolean) => void;
  private readonly disposeRoot: () => void;
  private refreshAbortController: AbortController | undefined;

  constructor(private readonly dataProvider: ObjectPropertiesProvider) {
    super();

    const [isOpen, setIsOpen] = createSignal(false);
    this.isOpen = isOpen;
    this.setIsOpen = setIsOpen;

    const [data, setData] = createSignal<ObjectPropertiesPanelData | undefined>(
      undefined
    );
    this.data = data;
    this.setData = setData;

    const [isLoading, setIsLoading] = createSignal(false);
    this.isLoading = isLoading;
    this.setIsLoading = setIsLoading;

    this.disposeRoot = createRoot((dispose) => {
      createEffect(
        // Refresh data when the panel is open and the selection changes
        on([this.isOpen, this.dataProvider.selectedByViewer], ([isOpen, _]) => {
          if (!isOpen) return;
          void this.refresh();
        })
      );
      return dispose;
    });
  }

  public async toggle(): Promise<void> {
    if (this.isOpen()) {
      this.close();
      return;
    }
    this.setIsOpen(true);
  }

  public close(): void {
    this.refreshAbortController?.abort();
    this.refreshAbortController = undefined;
    this.setIsOpen(false);
    this.setData(undefined);
    this.setIsLoading(false);
  }

  public async refresh(): Promise<void> {
    this.refreshAbortController?.abort();
    const controller = new AbortController();
    this.refreshAbortController = controller;
    this.setIsLoading(true);

    try {
      const data = await this.dataProvider.getPanelData({
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      this.setData(data);
    } catch (error) {
      if (isAbortError(error)) return;
      this.logger.error(
        'Error getting object properties panel data',
        asError(error)
      );
    } finally {
      // Only clear loading for the most recent refresh; a superseded call must
      // not turn off the indicator while its successor is still in flight.
      if (this.refreshAbortController === controller) {
        this.setIsLoading(false);
      }
    }
  }

  public async cleanup(): Promise<void> {
    this.refreshAbortController?.abort();
    this.refreshAbortController = undefined;
    this.disposeRoot();
  }
}
